import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  makeStyles,
  Theme,
  PrimaryButton,
  DefaultButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { CohortDetail } from 'App.types';
import { isEligibleForReminder, SiteInfo } from '../../hooks/useCohortNotifications';
import { isCohortEditable, canSendNotifications, canSendReminders } from '../../utils';

interface CohortStepperProps {
  cohort: CohortDetail;
  onSendNotifications?: () => void;
  onSendReminders?: () => void;
  loadingNotifications?: boolean;
  loadingReminders?: boolean;
  sites?: SiteInfo[];
  notificationData: {
    daysUntilDowntime: number;
    canSendNotifications: boolean;
    canSendReminders: boolean;
  };
  reminderStatusText?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  stepperContainer: {
    marginTop: theme.spacing(1),
  },
  stepper: {
    backgroundColor: 'transparent',
  },
  stepActions: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
}));

const CohortStepper: React.FC<CohortStepperProps> = ({
  cohort,
  onSendNotifications,
  onSendReminders,
  loadingNotifications = false,
  loadingReminders = false,
  sites = [],
  notificationData,
  reminderStatusText = '',
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  const currentStepValue = useMemo(() => {
    const isEditable = isCohortEditable(cohort.migration_status);
    const canSendNow = canSendNotifications(cohort.migration_status);

    if (isEditable && canSendNow) {
      return 0; // Planned - can send notifications
    }

    if (!isEditable && (!canSendReminders(cohort.migration_status) || !onSendReminders)) {
      return 2; // In progress or completed -> No buttons visible
    }

    return 1; // Notifications sent, can send reminders
  }, [cohort.migration_status, onSendReminders]);

  useEffect(() => {
    setCurrentStep(currentStepValue);
  }, [currentStepValue]);

  const showNotificationWarning = useMemo(
    () =>
      !notificationData.canSendNotifications &&
      notificationData.daysUntilDowntime < 15 &&
      currentStep === 0,
    [notificationData.canSendNotifications, notificationData.daysUntilDowntime, currentStep],
  );

  const hasEligibleSites = useMemo(
    () => sites.some((site) => isEligibleForReminder(site)),
    [sites],
  );

  const step1Description = cohort.enable_notifications
    ? t('cohorts.sendNotifications')
    : t('cohorts.sendNotificationsDisabled');

  const stepConfig = useMemo(
    () => [
      {
        key: 'notifications',
        label: t('cohorts.notifications'),
        iconId: 'step1Icon',
        description: currentStep > 0 ? t('cohorts.notificationsSentLabel') : step1Description,
        disabled:
          !notificationData.canSendNotifications || !isCohortEditable(cohort.migration_status),
        action: onSendNotifications,
        loading: loadingNotifications,
        buttonType: 'primary' as const,
        buttonText: t('cohorts.sendNotifications'),
        loadingText: t('cohorts.sendingNotifications', 'Sending Notifications...'),
        canSend: notificationData.canSendNotifications && cohort.enable_notifications,
        showWarning: showNotificationWarning,
        warningText: t('cohorts.notificationsNotAvailableDowntimeWarning'),
        contentText: t('cohorts.sendNotificationsDescription', { count: sites.length }),
        hideButton: false,
      },
      {
        key: 'reminders',
        label: `${t('cohorts.reminders')} (${t('cohorts.optional')})`,
        iconId: 'step2Icon',
        description: reminderStatusText || t('cohorts.remindersSentLabel'),
        disabled:
          !notificationData.canSendReminders ||
          isCohortEditable(cohort.migration_status) ||
          !onSendReminders,
        action: onSendReminders,
        loading: loadingReminders,
        buttonType: 'default' as const,
        buttonText: t('cohorts.sendReminders'),
        loadingText: t('cohorts.sendingReminders'),
        canSend: notificationData.canSendReminders && cohort.enable_notifications,
        showWarning: false,
        warningText: '',
        contentText: hasEligibleSites ? t('cohorts.sendRemindersDescription') : null,
        hideButton: !hasEligibleSites || !onSendReminders,
      },
      ...(sites.some((site) => site.site_status === 'DECLINED')
        ? [
            {
              key: 'declined',
              label: `${t('cohorts.declinedLabel')}`,
              iconId: 'step3Icon',
              description: t('cohorts.declinedDescription'),
              disabled: true,
              showWarning: true,
              warningText: 'This action must be taken before the cohort can proceed.',
            },
          ]
        : []),
    ],
    [
      t,
      currentStep,
      cohort.migration_status,
      cohort.enable_notifications,
      notificationData.canSendNotifications,
      notificationData.canSendReminders,
      onSendNotifications,
      onSendReminders,
      loadingNotifications,
      loadingReminders,
      showNotificationWarning,
      step1Description,
      reminderStatusText,
      hasEligibleSites,
      sites,
    ],
  );

  const handleStepClick = (stepIndex: number) => {
    const step = stepConfig[stepIndex];
    if (step.canSend && step.action && !step.hideButton) {
      step.action();
    }
  };

  const renderStepButton = (step: (typeof stepConfig)[0], stepIndex: number) => {
    if (currentStep !== stepIndex) return null;
    if (step.hideButton) return null;

    const ButtonComponent = step.buttonType === 'primary' ? PrimaryButton : DefaultButton;

    return (
      <div className={classes.stepActions}>
        <ButtonComponent onClick={step.action} disabled={!step.canSend || step.loading}>
          {step.loading ? step.loadingText : step.buttonText}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <div className={classes.stepperContainer}>
      <Stepper activeStep={currentStep} orientation="vertical" className={classes.stepper}>
        {stepConfig.map((step, index) => (
          <Step key={step.key}>
            <StepLabel
              disabled={step.disabled}
              onClick={() => handleStepClick(index)}
              stepNumber={`${step.label}: `}
              stepDescription={step.description}
              iconId={step.iconId}
              error={step.showWarning}
            />
            <StepContent>
              {step.showWarning ? (
                <Typography variant="body2" className={classes.errorText}>
                  {step.warningText}
                </Typography>
              ) : (
                <Typography variant="body2">{step.contentText}</Typography>
              )}
              {step.buttonText && renderStepButton(step, index)}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </div>
  );
};

export default CohortStepper;
